export type LayoutMode = 'desktop' | 'mobile-portrait' | 'mobile-landscape';

export interface ViewportMetrics {
  layoutMode: LayoutMode;
  stageWidth: number;
  stageHeight: number;
  supportScreenY: number;
  spawnScreenY: number;
  canvasCenterX: number;
  canvasCenterY: number;
  usablePlayHeight: number;
}

const DESKTOP_WIDTH = 960;
const DESKTOP_HEIGHT = 720;
const DESKTOP_SUPPORT_Y = 70;
const DESKTOP_SPAWN_Y = 620;
export const MOBILE_AIMING_EDGE_GAP = 12;
export const MOBILE_AIMING_ENVELOPE_SCALE = 0.72;
const MIN_RENDER_PIXEL_RATIO = 2;
const MAX_RENDER_PIXEL_RATIO = 2;

export function renderPixelRatioForDevice(devicePixelRatio: number): number {
  if (!Number.isFinite(devicePixelRatio) || devicePixelRatio < 1) return MIN_RENDER_PIXEL_RATIO;
  return Math.min(MAX_RENDER_PIXEL_RATIO, Math.max(MIN_RENDER_PIXEL_RATIO, devicePixelRatio));
}

interface AimingDimensions {
  width: number;
  height: number;
}

export function maximumAimingRadius(definitions: readonly AimingDimensions[]): number {
  return definitions.reduce((maximum, definition) => {
    const radius = Math.hypot(definition.width, definition.height) / 2;
    return Number.isFinite(radius) ? Math.max(maximum, radius) : maximum;
  }, 0);
}

function finiteDimension(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

export function layoutModeForViewport(width: number, height: number): LayoutMode {
  const safeWidth = finiteDimension(width, DESKTOP_WIDTH);
  const safeHeight = finiteDimension(height, DESKTOP_HEIGHT);
  if (safeWidth <= 600 && safeHeight >= safeWidth) return 'mobile-portrait';
  if (safeWidth <= 960 && safeHeight <= 560 && safeWidth > safeHeight) return 'mobile-landscape';
  return 'desktop';
}

export function viewportMetricsForStage(
  layoutMode: LayoutMode,
  measuredStageWidth: number,
  measuredStageHeight: number,
  maximumRosterAimingRadius = 0,
): ViewportMetrics {
  if (layoutMode === 'desktop') {
    const spawnScreenY = Math.min(
      DESKTOP_SPAWN_Y,
      DESKTOP_HEIGHT - Math.max(0, maximumRosterAimingRadius) - MOBILE_AIMING_EDGE_GAP,
    );
    return {
      layoutMode,
      stageWidth: DESKTOP_WIDTH,
      stageHeight: DESKTOP_HEIGHT,
      supportScreenY: DESKTOP_SUPPORT_Y,
      spawnScreenY,
      canvasCenterX: DESKTOP_WIDTH / 2,
      canvasCenterY: DESKTOP_HEIGHT / 2,
      usablePlayHeight: spawnScreenY - DESKTOP_SUPPORT_Y,
    };
  }

  const stageWidth = finiteDimension(measuredStageWidth, layoutMode === 'mobile-portrait' ? 390 : 844);
  const stageHeight = finiteDimension(measuredStageHeight, layoutMode === 'mobile-portrait' ? 640 : 270);
  const supportScreenY = layoutMode === 'mobile-portrait'
    ? Math.min(56, Math.max(40, stageHeight * 0.08))
    : Math.min(40, Math.max(24, stageHeight * 0.1));
  const baseBottomInset = layoutMode === 'mobile-portrait' ? 96 : 68;
  const aimingSafeBottomInset = Math.max(0, maximumRosterAimingRadius) * MOBILE_AIMING_ENVELOPE_SCALE
    + MOBILE_AIMING_EDGE_GAP;
  const spawnScreenY = stageHeight - Math.max(baseBottomInset, aimingSafeBottomInset);

  return {
    layoutMode,
    stageWidth,
    stageHeight,
    supportScreenY,
    spawnScreenY,
    canvasCenterX: stageWidth / 2,
    canvasCenterY: stageHeight / 2,
    usablePlayHeight: spawnScreenY - supportScreenY,
  };
}

export const DESKTOP_VIEWPORT_METRICS = viewportMetricsForStage('desktop', DESKTOP_WIDTH, DESKTOP_HEIGHT);
